import {
  Component,
  ElementRef,
  Input,
  ViewChild,
} from "@angular/core";
import { Company } from "@core/models";
import { NgxToastrService, UserService } from "@core/services";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { finalize } from "rxjs/operators";

@Component({
  selector: "app-import-users",
  templateUrl: "./import-users.component.html",
  styleUrls: ["./import-users.component.scss"],
})
export class ImportUsersComponent {
  @Input() company: Company;
  @Input() departments: any[] = [];
  @Input() authorUuid: string;

  @ViewChild("fileInput")
  fileInput?: ElementRef<HTMLInputElement>;

  selectedFile: File | null = null;
  loading = false;

  constructor(
    public activeModal: NgbActiveModal,
    private userService: UserService,
    private ngxToastrService: NgxToastrService,
    private translateService: TranslateService
  ) {}

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;

    if (!file) {
      this.selectedFile = null;
      return;
    }

    const extension = file.name
      .split(".")
      .pop()
      ?.toLowerCase();

    const allowedExtensions = [
      "xlsx",
      "xls",
      "csv",
    ];

    if (
      !extension ||
      !allowedExtensions.includes(extension)
    ) {
      this.resetSelectedFile();

      this.showImportError(
        "user.import.allowed-file-types"
      );

      return;
    }

    const maxFileSize = 5 * 1024 * 1024;

    if (file.size > maxFileSize) {
      this.resetSelectedFile();

      this.showImportError(
        "user.import.max-file-size"
      );

      return;
    }

    this.selectedFile = file;
  }

  importUsers(): void {
    if (
      this.loading ||
      !this.selectedFile ||
      !this.company?.uuid
    ) {
      return;
    }

    this.loading = true;

    this.userService
      .importUsers(
        this.company.uuid,
        this.selectedFile
      )
      .pipe(
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (result) => {
          const total = Number(result?.total || 0);
          const created = Number(
            result?.created || 0
          );
          const failed = Number(
            result?.failed || 0
          );

          const allUsersCreated =
            total > 0 &&
            created === total &&
            failed === 0 &&
            !result?.rolledBack;

          if (allUsersCreated) {
            this.ngxToastrService.typeSuccess(
              null,
              this.translateService.instant(
                "user.import.success",
                { count: created }
              )
            );

            this.activeModal.close({
              imported: created,
            });

            return;
          }

          this.ngxToastrService.typeInfo(
            this.translateService.instant(
              "common.error"
            ),
            this.getImportErrorMessage(result),
            10000
          );

          this.resetSelectedFile();
        },

        error: (error) => {
          const body = error?.error;

          const message =
            Array.isArray(body?.errors) &&
            body.errors.length > 0
              ? this.getImportErrorMessage(body)
              : this.translateBackendMessage(
                  body?.message || body?.text
                );

          this.ngxToastrService.typeInfo(
            this.translateService.instant("common.error"),
            message,
            10000
          );

          this.resetSelectedFile();
        },
      });
  }

  private getImportErrorMessage(
    result: any
  ): string {
    const errors = Array.isArray(result?.errors)
      ? result.errors
      : [];

    const uniqueMessages = Array.from(
      new Set<string>(
        errors
          .map((error) =>
            this.translateImportError(error)
          )
          .filter(
            (message): message is string =>
              typeof message === "string" &&
              message.trim().length > 0
          )
          .map((message) => message.trim())
      )
    );

    if (uniqueMessages.length === 0) {
      return this.translateService.instant(
        result?.rolledBack
          ? "user.import.rolled-back"
          : "user.import.generic-error"
      );
    }

    if (uniqueMessages.length === 1) {
      return uniqueMessages[0];
    }

    const visibleMessages =
      uniqueMessages.slice(0, 3);

    const hiddenMessages =
      uniqueMessages.length -
      visibleMessages.length;

    let message = visibleMessages.join(" · ");

    if (hiddenMessages > 0) {
      message += ` · ${this.translateService.instant(
        "user.import.additional-errors",
        { count: hiddenMessages }
      )}`;
    }

    return message;
  }

  private translateImportError(error: any): string {
    const translationsByCode: Record<string, string> = {
      EMAIL_ALREADY_EXISTS:
        "user.import.errors.email-already-exists",
      DUPLICATE_EMAIL_IN_FILE:
        "user.import.errors.duplicate-email-in-file",
      INVALID_NATIONALITY:
        "user.import.errors.invalid-nationality",
      CREATE_ERROR:
        "user.import.errors.create-error",
      ACTIVATION_EMAIL_ERROR:
        "user.import.errors.activation-email-error",
      OKTICKET_ERROR:
        "user.import.errors.okticket-error",
    };

    let message: string;

    if (error?.code === "VALIDATION_ERROR") {
      const details: any[] = Array.isArray(error?.details)
        ? error.details
        : [];

      const requiredTypes = [
        "any.required",
        "string.empty",
      ];

      const requiredFields = new Set(
        details
          .filter((detail) =>
            requiredTypes.includes(detail.type)
          )
          .map((detail) => detail.field)
      );

      const messages = details
        .filter((detail) => {
          return (
            !requiredFields.has(detail.field) ||
            requiredTypes.includes(detail.type)
          );
        })
        .map((detail) =>
          this.translateValidationDetail(detail)
        );

      message = Array.from(new Set(messages)).join("; ");

      if (!message) {
        message = this.translateService.instant(
          "user.import.errors.validation-error"
        );
      }
    } else {
      const translationKey =
        translationsByCode[error?.code];

      message = translationKey
        ? this.translateService.instant(translationKey)
        : this.translateBackendMessage(error?.message);
    }

    const context: string[] = [];

    if (
      Number.isInteger(error?.row) &&
      error.row > 0
    ) {
      context.push(
        this.translateService.instant(
          "user.import.row",
          { row: error.row }
        )
      );
    }

    if (
      typeof error?.email === "string" &&
      error.email.trim()
    ) {
      context.push(error.email.trim());
    }

    return context.length > 0
      ? `${context.join(" · ")}: ${message}`
      : message;
  }

  private translateValidationDetail(detail: any): string {
    const fieldKey =
      `user.import.fields.${detail.field}`;

    const translatedField =
      this.translateService.instant(fieldKey);

    const field = translatedField !== fieldKey
      ? translatedField
      : this.translateService.instant(
          "user.import.fields.unknown"
        );

    const translationsByType: Record<string, string> = {
      "any.required": "required",
      "string.empty": "required",
      "string.min": "min",
      "string.max": "max",
      "string.length": "length",
      "string.email": "email",
      "string.base": "text",
      "any.only": "invalid",
      "any.allowOnly": "invalid",
      "string.pattern.base": "invalid",
      "string.regex.base": "invalid",
      "date.base": "date",
      "date.format": "date",
      "boolean.base": "invalid",
    };

    const rule =
      translationsByType[detail.type] || "invalid";

    return this.translateService.instant(
      `user.import.validation.${rule}`,
      {
        field,
        limit: detail.limit,
      }
    );
  }

  private translateBackendMessage(message: unknown): string {
    const translationsByMessage: Record<string, string> = {
      "Ya existe un usuario con este email":
        "user.import.errors.email-already-exists",

      "El usuario está repetido dentro del archivo":
        "user.import.errors.duplicate-email-in-file",

      "El email está repetido dentro del archivo":
        "user.import.errors.duplicate-email-in-file",

      "No se pudo crear el usuario. No se ha importado ningún usuario.":
        "user.import.errors.create-error",

      "La nacionalidad seleccionada no es válida":
        "user.import.errors.invalid-nationality",

      "Debes seleccionar un archivo Excel o CSV":
        "user.import.select-file",

      "Solo se permiten archivos Excel o CSV":
        "user.import.allowed-file-types",

      "No se pudo leer el archivo":
        "user.import.invalid-file",

      "El archivo no contiene ninguna hoja":
        "user.import.invalid-file",

      "El archivo no contiene usuarios":
        "user.import.errors.empty-file",

      "No se ha encontrado la empresa":
        "user.import.errors.company-not-found",

      "No se pueden importar más de 1000 usuarios a la vez":
        "user.import.errors.too-many-users",

      "No se pudo procesar el archivo de usuarios":
        "user.import.generic-error",
    };

    if (typeof message === "string") {
      const translationKey =
        translationsByMessage[message];

      if (translationKey) {
        return this.translateService.instant(
          translationKey
        );
      }

      if (message.startsWith("user.import.")) {
        const translated =
          this.translateService.instant(message);

        if (translated !== message) {
          return translated;
        }
      }

      const missingHeadersPrefix =
        "Faltan las siguientes columnas obligatorias:";

      if (message.startsWith(missingHeadersPrefix)) {
        return this.translateService.instant(
          "user.import.errors.missing-headers",
          {
            columns: message
              .slice(missingHeadersPrefix.length)
              .trim(),
          }
        );
      }
    }

    return this.translateService.instant(
      "user.import.generic-error"
    );
  }

  private showImportError(
    translationKey: string
  ): void {
    this.ngxToastrService.typeInfo(
      this.translateService.instant(
        "common.error"
      ),
      this.translateService.instant(
        translationKey
      )
    );
  }

  private resetSelectedFile(): void {
    this.selectedFile = null;

    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = "";
    }
  }

  downloadTemplate(): void {
    const link = document.createElement("a");

    link.href =
      "assets/templates/import-users-template.xlsx";

    link.download =
      "import-users-template.xlsx";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}